import React, { useCallback, useEffect, useState } from 'react';

import styled from 'styled-components';

import { CommunityPreferenceType, ReviewAlertPreference } from 'types/communities';

import CommunityReviewAlertsTabContent from './CommunityReviewAlertsTabContent';
import CommunityReviewAlertsTabs from './CommunityReviewAlertsTabs';
import {
  CommunityReviewAlertScope,
  createCategoryAlertPreference,
  DEFAULT_ALERT_MODE_SETTINGS_BY_SCOPE,
  ReviewAlertFilterField,
} from './constants';
import { CommunityReviewAlertScopeSettings } from './types';
import { areScopeSettingsEqual, buildScopeAlertModeSettings } from './utils';
import DiscardChangesModal from '../DiscardChangesModal';

const Container = styled.div`
  padding-top: 10px;
  padding-right: 20px;
  min-height: 350px;
  max-width: 442px;
`;

type StatusOption = {
  label: string;
  value: number;
};

type StatusElements = {
  ON: StatusOption;
  OFF: StatusOption;
};

type AlertFlags = Record<CommunityReviewAlertScope, boolean>;

type Props = {
  isOpen: boolean;
  activeTab: CommunityReviewAlertScope;
  communitiesToModify: CommunityPreferenceType[];
  statusElements: StatusElements;
  onOverallStatusChange: (nextStatus: number) => void;
  onActiveTabChange?: (tab: CommunityReviewAlertScope) => void;
  onAlertFlagsChange?: (flags: AlertFlags) => void;
  onAlertModeSettingsChange?: (settings: Record<CommunityReviewAlertScope, CommunityReviewAlertScopeSettings>) => void;
};

const CommunityReviewAlertsRecipientsSettings = ({
  isOpen,
  activeTab,
  communitiesToModify,
  statusElements,
  onOverallStatusChange,
  onActiveTabChange,
  onAlertFlagsChange,
  onAlertModeSettingsChange,
}: Props) => {
  const [alertFlags, setAlertFlags] = useState<Record<CommunityReviewAlertScope, boolean>>({
    [CommunityReviewAlertScope.COMMUNITY]: false,
    [CommunityReviewAlertScope.COMPETITOR]: false,
  });
  const [alertModeSettings, setAlertModeSettings] = useState<
    Record<CommunityReviewAlertScope, CommunityReviewAlertScopeSettings>
  >(DEFAULT_ALERT_MODE_SETTINGS_BY_SCOPE);
  const [initialAlertFlags, setInitialAlertFlags] = useState<Record<CommunityReviewAlertScope, boolean>>({
    [CommunityReviewAlertScope.COMMUNITY]: false,
    [CommunityReviewAlertScope.COMPETITOR]: false,
  });
  const [initialAlertModeSettings, setInitialAlertModeSettings] = useState<
    Record<CommunityReviewAlertScope, CommunityReviewAlertScopeSettings>
  >(DEFAULT_ALERT_MODE_SETTINGS_BY_SCOPE);
  const [discardModalOpen, setDiscardModalOpen] = useState(false);
  const [pendingTab, setPendingTab] = useState<CommunityReviewAlertScope | null>(null);

  const updateOverallStatus = useCallback(
    (flags: AlertFlags) => {
      const hasAnyOn = flags[CommunityReviewAlertScope.COMMUNITY] || flags[CommunityReviewAlertScope.COMPETITOR];
      onOverallStatusChange(hasAnyOn ? statusElements.ON.value : statusElements.OFF.value);
    },
    [onOverallStatusChange, statusElements]
  );

  const switchTab = useCallback(
    (nextTab: CommunityReviewAlertScope) => {
      onActiveTabChange?.(nextTab);
    },
    [onActiveTabChange]
  );

  const isTabDirty = (tab: CommunityReviewAlertScope) => {
    const currentFlag = alertFlags[tab];
    const initialFlag = initialAlertFlags[tab];
    const currentModeSettings = alertModeSettings[tab];
    const initialMode = initialAlertModeSettings[tab];

    if (!currentFlag && !initialFlag) {
      return false;
    }

    return currentFlag !== initialFlag || !areScopeSettingsEqual(currentModeSettings, initialMode);
  };

  const requestTabSwitch = (nextTab: CommunityReviewAlertScope) => {
    if (nextTab === activeTab) {
      return;
    }

    if (isTabDirty(activeTab)) {
      setPendingTab(nextTab);
      setDiscardModalOpen(true);
      return;
    }

    switchTab(nextTab);
  };

  useEffect(() => {
    if (isOpen) {
      setDiscardModalOpen(false);
      setPendingTab(null);
      switchTab(CommunityReviewAlertScope.COMMUNITY);
    } else {
      switchTab(CommunityReviewAlertScope.COMMUNITY);
    }
  }, [isOpen, switchTab]);

  useEffect(() => {
    const firstCommunity = communitiesToModify[0];
    const communityOn = Boolean(firstCommunity?.preferences?.community_review_alert_notifications);
    const competitorOn = Boolean(firstCommunity?.preferences?.competitor_review_alert_notifications);

    const nextAlertFlags = {
      [CommunityReviewAlertScope.COMMUNITY]: communityOn,
      [CommunityReviewAlertScope.COMPETITOR]: competitorOn,
    };
    const nextAlertModeSettings = {
      [CommunityReviewAlertScope.COMMUNITY]: communityOn
        ? buildScopeAlertModeSettings(firstCommunity?.preferences?.community_review_alert_preferences)
        : { ...DEFAULT_ALERT_MODE_SETTINGS_BY_SCOPE[CommunityReviewAlertScope.COMMUNITY] },
      [CommunityReviewAlertScope.COMPETITOR]: competitorOn
        ? buildScopeAlertModeSettings(firstCommunity?.preferences?.competitor_review_alert_preferences)
        : { ...DEFAULT_ALERT_MODE_SETTINGS_BY_SCOPE[CommunityReviewAlertScope.COMPETITOR] },
    };

    setAlertFlags(nextAlertFlags);
    setAlertModeSettings(nextAlertModeSettings);
    setInitialAlertFlags(nextAlertFlags);
    setInitialAlertModeSettings(nextAlertModeSettings);
    onAlertFlagsChange?.(nextAlertFlags);
    onAlertModeSettingsChange?.(nextAlertModeSettings);
    updateOverallStatus(nextAlertFlags);
  }, [communitiesToModify, onAlertFlagsChange, onAlertModeSettingsChange, updateOverallStatus]);

  const onActiveTabStatusChange = (nextStatus: number) => {
    const isOn = nextStatus === statusElements.ON.value;

    setAlertFlags((prev) => {
      const next = { ...prev, [activeTab]: isOn };

      onAlertFlagsChange?.(next);
      updateOverallStatus(next);
      return next;
    });
  };

  const onAlertModeChange = (mode: number) => {
    setAlertModeSettings((prev) => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        mode,
      },
    }));
  };

  const onOverallFilterChange = (
    field: ReviewAlertFilterField,
    value: ReviewAlertPreference[ReviewAlertFilterField]
  ) => {
    setAlertModeSettings((prev) => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        overall: {
          ...prev[activeTab].overall,
          [field]: value,
        },
      },
    }));
  };

  const onCategoryToggle = (reviewCategoryId: number, checked: boolean) => {
    setAlertModeSettings((prev) => {
      const currentTabSettings = prev[activeTab];
      const hasPreference = Boolean(currentTabSettings.category_settings[reviewCategoryId]);
      const selectedCategoryIds = checked
        ? Array.from(new Set([...currentTabSettings.selected_category_ids, reviewCategoryId]))
        : currentTabSettings.selected_category_ids.filter((id) => id !== reviewCategoryId);

      return {
        ...prev,
        [activeTab]: {
          ...currentTabSettings,
          selected_category_ids: selectedCategoryIds,
          category_settings: hasPreference
            ? currentTabSettings.category_settings
            : {
                ...currentTabSettings.category_settings,
                [reviewCategoryId]: createCategoryAlertPreference(reviewCategoryId),
              },
        },
      };
    });
  };

  const onCategoryFilterChange = (
    reviewCategoryId: number,
    field: ReviewAlertFilterField,
    value: ReviewAlertPreference[ReviewAlertFilterField]
  ) => {
    setAlertModeSettings((prev) => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        category_settings: {
          ...prev[activeTab].category_settings,
          [reviewCategoryId]: {
            ...(prev[activeTab].category_settings[reviewCategoryId] ?? createCategoryAlertPreference(reviewCategoryId)),
            [field]: value,
          },
        },
      },
    }));
  };

  const keepEditingCurrentTab = () => {
    setDiscardModalOpen(false);
    setPendingTab(null);
  };

  const discardCurrentTabChangesAndSwitch = () => {
    if (!pendingTab) {
      setDiscardModalOpen(false);
      return;
    }

    const nextFlags = {
      ...alertFlags,
      [activeTab]: initialAlertFlags[activeTab],
    };
    const nextModes = {
      ...alertModeSettings,
      [activeTab]: initialAlertModeSettings[activeTab],
    };

    setAlertFlags(nextFlags);
    setAlertModeSettings(nextModes);
    onAlertFlagsChange?.(nextFlags);
    onAlertModeSettingsChange?.(nextModes);
    updateOverallStatus(nextFlags);
    setDiscardModalOpen(false);
    setPendingTab(null);
    switchTab(pendingTab);
  };

  useEffect(() => {
    onAlertModeSettingsChange?.(alertModeSettings);
  }, [alertModeSettings, onAlertModeSettingsChange]);

  return (
    <Container>
      <CommunityReviewAlertsTabs activeTab={activeTab} alertFlags={alertFlags} onTabClick={requestTabSwitch} />
      <CommunityReviewAlertsTabContent
        activeTab={activeTab}
        alertFlags={alertFlags}
        activeTabSettings={alertModeSettings[activeTab]}
        statusElements={statusElements}
        onActiveTabStatusChange={onActiveTabStatusChange}
        onAlertModeChange={onAlertModeChange}
        onOverallFilterChange={onOverallFilterChange}
        onCategoryToggle={onCategoryToggle}
        onCategoryFilterChange={onCategoryFilterChange}
      />
      <DiscardChangesModal
        open={discardModalOpen}
        onDiscard={discardCurrentTabChangesAndSwitch}
        onKeepEditing={keepEditingCurrentTab}
      />
    </Container>
  );
};

export default CommunityReviewAlertsRecipientsSettings;
